/**
 * TrainingPage — View assigned training courses and mark as completed.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, Button, Chip, Stack,
  Skeleton,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  School as TrainingIcon, CheckCircle as CompletedIcon,
  OpenInNew as LinkIcon,
} from '@mui/icons-material';
import EmptyState from '@/components/common/EmptyState';
import apiClient from '@/api/client';
import { formatDate } from '@/utils/format';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermission } from '@/hooks/usePermission';
import PageHelpIcon from '@/components/PageHelpIcon';
import { TYPE } from '@/theme/typography';
import { STATUS_COLORS as TOKEN_STATUS_COLORS } from '@/theme/colorTokens';
import { usePaletteAccents } from '@/hooks/usePaletteAccents';
import { KpiStatCard, ScoreGaugeCard } from '@/components/panel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  assigned: 'info',
  in_progress: 'warning',
  completed: 'success',
  overdue: 'error',
  expired: 'default',
};

// CATEGORY_COLORS moved inside component for palette awareness

export default function TrainingPage({ embedded }: { embedded?: boolean } = {}) {
  const { hasPermission } = usePermission();
  const canAssign = hasPermission('hr.write');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_assignOpen, setAssignOpen] = useState(false);
  const ACCENT = usePaletteAccents();
  const CATEGORY_COLORS: Record<string, string> = {
    compliance: ACCENT.red,
    onboarding: ACCENT.blue,
    skills: ACCENT.green,
    leadership: ACCENT.orange,
    custom: TOKEN_STATUS_COLORS.default,
  };
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();

  const trainingQuery = useQuery({
    queryKey: ['my-training'],
    queryFn: () => apiClient.get('/employee/training').then((r) => r.data),
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.patch(`/employee/training/${id}/complete`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-training'] });
      showSuccess('Training marked as completed');
    },
    onError: (err: any) => showError(err?.response?.data?.detail || 'Failed'),
  });

  const items = (trainingQuery.data as any)?.items || [];
  const completed = items.filter((i: any) => i.status === 'completed').length;
  const inProgress = items.filter((i: any) => i.status === 'in_progress' || i.status === 'assigned').length;
  const overdue = items.filter((i: any) => i.status === 'overdue').length;
  const total = items.length;
  const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TrainingIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h5" fontWeight={TYPE.semibold}>My Training</Typography>
              <PageHelpIcon pageKey="hr.training" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {completed}/{total} courses completed
            </Typography>
          </Box>
        </Stack>
      )}

      {total > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, md: 4 }}>
                <KpiStatCard label="Assigned" value={total} icon={<TrainingIcon fontSize="small" />} accentValue />
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <KpiStatCard label="In Progress" value={inProgress} icon={<HourglassEmptyIcon fontSize="small" />} />
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <KpiStatCard label="Completed" value={completed} icon={<CompletedIcon fontSize="small" />} />
              </Grid>
              {overdue > 0 && (
                <Grid size={{ xs: 6, md: 4 }}>
                  <KpiStatCard label="Overdue" value={overdue} icon={<WarningAmberIcon fontSize="small" />} trend="down" delta="action needed" />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ScoreGaugeCard
              title="Training Completion"
              info="Completed courses as a share of all assigned courses"
              score={completionPct}
              percent={completionPct}
              statusValue={completed}
              statusLabel="Completed"
              secondaryValue={total}
              secondaryLabel="Assigned"
              legendLabel="Completed"
              legendValue={`${inProgress} remaining`}
            />
          </Grid>
        </Grid>
      )}

      {trainingQuery.isLoading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}><Skeleton variant="rounded" height={180} /></Grid>)}
        </Grid>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<TrainingIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
          title="No training assigned"
          description="Assign training modules to employees to track development."
          actionLabel={canAssign ? 'Assign Training' : undefined}
          onAction={canAssign ? () => setAssignOpen(true) : undefined}
        />
      ) : (
        <Grid container spacing={2}>
          {items.map((item: any) => (
            <Grid key={item.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ height: 4, bgcolor: CATEGORY_COLORS[item.category] || TOKEN_STATUS_COLORS.default }} />
                <CardContent sx={{ flex: 1 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="subtitle2" fontWeight={TYPE.semibold}>{item.course_name}</Typography>
                      <Chip size="small" label={item.status} color={STATUS_COLORS[item.status] || 'default'} />
                    </Stack>
                    <Chip size="small" label={item.category} variant="outlined" sx={{ width: 'fit-content' }} />
                    {item.due_date && (
                      <Typography variant="caption" color={item.status === 'overdue' ? 'error.main' : 'text.secondary'}>
                        {item.status === 'overdue' ? 'Overdue — was due' : 'Due'}: {formatDate(item.due_date)}
                      </Typography>
                    )}
                    {item.completed_at && (
                      <Typography variant="caption" color="success.main">
                        Completed: {formatDate(item.completed_at)}
                        {item.score !== null && ` — Score: ${item.score}%`}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                      {item.content_url && (
                        <Button size="small" variant="outlined" startIcon={<LinkIcon />}
                          href={item.content_url} target="_blank" rel="noopener">
                          Open
                        </Button>
                      )}
                      {item.status !== 'completed' && (
                        <Button size="small" variant="contained" startIcon={<CompletedIcon />}
                          onClick={() => completeMutation.mutate(item.id)}
                          disabled={completeMutation.isPending}>
                          Complete
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
